frappe.provide('ftcore');

/**
 * Money arithmetic helpers. The browser twin of `ftcore/utils/amounts.py` — the two MUST stay
 * identical, so there is one definition of "floored at zero" rather than one per call site.
 */

/**
 * Coerce a value to a number the way frappe's Python `flt` does — which `parseFloat` does not.
 *
 * `flt("1,234.56")` strips digit separators and reads 1234.56, where `parseFloat` stops at the
 * comma and returns 1. `flt("12abc")` is 0 where `parseFloat` returns 12. `null`, `undefined`, `""`
 * and non-numeric strings all read as 0.
 *
 * @param {*} value - Any amount: number, numeric string, null or undefined.
 * @returns {number} The number, or 0 when it is unusable.
 */
function to_number(value) {
    if (typeof value === "string") value = value.replace(/,/g, "");
    const amount = Number(value);
    return isNaN(amount) ? 0 : amount;
}

/**
 * Coerce an amount to a number that is never negative. Non-numeric input reads as 0.
 *
 * Use on the RESULT of a subtraction, not its inputs:
 *     row.total_cost = ftcore.floor_at_zero(row.subtotal - row.discount);
 *
 * @param {*} value - Any amount: number, numeric string, null or undefined.
 * @returns {number} The amount, or 0 when it is negative or unusable.
 */
ftcore.floor_at_zero = function (value) {
    return Math.max(0, to_number(value));
};

/**
 * Decimal places money is stored and displayed at. Twin of `CURRENCY_PRECISION` in
 * `ftcore/utils/amounts.py`.
 */
ftcore.CURRENCY_PRECISION = 2;

/**
 * Round an amount to currency precision. The browser twin of `ftcore.utils.amounts.round_money` —
 * use it on every money value the page displays or submits, at the same points the server rounds.
 *
 * Apply to derived money, not raw per-unit inputs, and round LAST, after flooring/capping.
 *
 * Delegates to frappe's global `flt` so half-cent rounding agrees with the server. The `toFixed`
 * fallback (for a context without `flt` loaded) rounds half away from zero instead, so it can
 * differ from the server on an exact half-cent.
 *
 * @param {*} value - Any amount: number, numeric string, null or undefined.
 * @param {number} [precision] - Decimal places. Defaults to `ftcore.CURRENCY_PRECISION`.
 * @returns {number} The rounded amount, or 0 when the value is unusable.
 */
ftcore.round_money = function (value, precision) {
    const amount = to_number(value);
    const dp = precision === undefined || precision === null ? ftcore.CURRENCY_PRECISION : precision;
    return typeof flt === "function" ? flt(amount, dp) : Number(amount.toFixed(dp));
};

/**
 * Discount an amount by a percentage, floored at zero and rounded to currency precision.
 *
 * The browser twin of `ftcore.utils.amounts.apply_percentage_discount` — the two MUST stay identical.
 *
 * @param {*} base_amount - The amount before the discount.
 * @param {*} discount_percentage - The discount, as a percentage. Clamped to 0-100.
 * @returns {number} The discounted amount, floored at zero and rounded to `ftcore.CURRENCY_PRECISION`.
 */
ftcore.apply_percentage_discount = function (base_amount, discount_percentage) {
    const clamped_percentage = Math.min(Math.max(to_number(discount_percentage), 0), 100);
    return ftcore.round_money(ftcore.floor_at_zero(to_number(base_amount) * (1 - clamped_percentage / 100)));
};
